import { Button, Css, ModalBody, ModalFooter, ModalHeader, useModal, useSnackbar } from "@homebound/beam";
import { ChangeEventLineItemType } from "src/generated/graphqlTypes";
import { useZeroChangeEventBudgetChangesMutation } from "src/routes/projects/change-events/components/ZeroBudgetChangesModal.generated";

type SelectedChangeEventLineItem = { id: string; type: ChangeEventLineItemType; totalCostInCents: number };

type ZeroBudgetChangesModalProps = { selectedItems: SelectedChangeEventLineItem[] };

/**
 * Confirms zeroing out the cost change on the selected change event line items.
 *
 * This is only meant for change events where the budget has already been corrected elsewhere, and the
 * change event exists solely to fix the options configuration and the LFS.
 */
export function ZeroBudgetChangesModal(props: ZeroBudgetChangesModalProps) {
  const { selectedItems } = props;
  const [zeroBudgetChanges] = useZeroChangeEventBudgetChangesMutation();
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();

  // Line items already at $0 don't need saving, and shouldn't have their cost source flipped to Manual.
  // Removes are skipped, as their budget change always zeroes out the project item's current budget.
  const removeCount = selectedItems.count((celi) => celi.type === ChangeEventLineItemType.Remove);
  const itemsToZero = selectedItems.filter(
    (celi) => celi.type !== ChangeEventLineItemType.Remove && celi.totalCostInCents !== 0,
  );

  return (
    <>
      <ModalHeader>Zero all budget changes?</ModalHeader>
      <ModalBody>
        <p css={Css.md.gray900.mb2.$}>
          {itemsToZero.isEmpty
            ? `None of the ${selectedItems.length} selected line ${selectedItems.length === 1 ? "item has" : "items have"} a budget change that can be zeroed out.`
            : `This will set the total cost change to $0 on ${itemsToZero.length} of the ${selectedItems.length} selected line ${selectedItems.length === 1 ? "item" : "items"}, removing their budget impact.`}
        </p>
        {removeCount > 0 && (
          <p css={Css.sm.gray700.mb2.$}>
            {removeCount === 1 ? "1 Remove line item is" : `${removeCount} Remove line items are`} skipped, as Remove
            line items always zero out their project item's current budget.
          </p>
        )}
        {!itemsToZero.isEmpty && (
          <>
            <p css={Css.sm.gray700.mb2.$}>Their cost source will be changed to Manual.</p>
            <p css={Css.sm.gray700.$}>
              Only do this when the budget has already been corrected elsewhere and this change event exists solely to
              fix the options configuration and the LFS. This cannot be undone.
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          label="Zero Budget Changes"
          variant="danger"
          disabled={itemsToZero.isEmpty && "There are no budget changes to zero out."}
          onClick={async () => {
            await zeroBudgetChanges({
              variables: {
                input: {
                  changeEventLineItems: itemsToZero.map((celi) => ({
                    id: celi.id,
                    totalCostInCents: 0,
                    // Zeroing the cost detaches the line item from its bid contract, i.e. "Manual" cost source
                    proposedBidContractLineItemId: null,
                  })),
                },
              },
            });
            triggerNotice({ message: "All budget changes have been set to $0", icon: "success" });
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}
